import { useState, type ReactElement } from 'react';

import { ApiError } from '../lib/api';
import { useAuth } from '../store/auth';

/**
 * What an unconfirmed account sees instead of the app.
 *
 * This screen is the reason the API refuses with `email_not_verified` and not a
 * bare 403: the client can only offer the fix — resend the link — if it can
 * tell "confirm your e-mail" apart from "this is not yours".
 *
 * The session is real, so signing out is offered too: someone who typed the
 * wrong address needs a way back to the login screen.
 */

const GENERIC_FAILURE = 'Não foi possível reenviar agora. Tente de novo em instantes.';
const SENT = 'Enviamos outro link. Confira sua caixa de entrada.';

export function VerifyPendingPage(): ReactElement {
  const { user, logout, resendVerification } = useAuth();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  async function handleResend(): Promise<void> {
    setNotice(null);
    setError(null);
    setSending(true);

    try {
      await resendVerification();
      setNotice(SENT);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
    } finally {
      setSending(false);
    }
  }

  return (
    <main className="auth-screen">
      <div className="auth-card">
        <h1 className="auth-title">Confirme seu e-mail</h1>
        <p className="auth-subtitle">
          Mandamos um link para <strong>{user?.email ?? 'seu e-mail'}</strong>. Confirme para
          liberar as conversas e os canais de voz.
        </p>

        {error !== null && (
          <p className="auth-alert" role="alert">
            {error}
          </p>
        )}

        {notice !== null && <p className="auth-footnote">{notice}</p>}

        <button
          className="auth-submit"
          type="button"
          disabled={sending}
          onClick={() => {
            void handleResend();
          }}
        >
          {sending ? 'Enviando…' : 'Reenviar e-mail'}
        </button>

        <p className="auth-footnote">
          Errou o endereço?{' '}
          <button
            className="auth-linklike"
            type="button"
            onClick={() => {
              void logout();
            }}
          >
            Sair
          </button>{' '}
          e cadastre de novo com o convite.
        </p>
      </div>
    </main>
  );
}

export default VerifyPendingPage;
